/**
 * useGlobalWebSocketHandler — Global WebSocket cache event handler.
 *
 * Called from ModelLayoutInner so it is alive across ALL model pages
 * (Architecture, Dashboard, Audit, Financial Statements, etc.).
 *
 * Handles cache-level operations that must survive page navigation:
 * - Invalidating section-data queries on compute completion
 * - Patching the scenario-tree cache (clear output errors, apply input errors)
 * - Updating the dashboardComputed flag
 *
 * FME-specific actions (reload visible section, fetch error list for modal)
 * remain in the FME-local useWebSocketHandler hook.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { patchTreeCache, type EditorState } from './queries/useScenarioTree';
import { SECTION_DATA_KEY } from './queries/useSectionLoader';
import { clearTreeErrors } from '../utils/treeErrors';

interface UseGlobalWebSocketHandlerParams {
    lastJsonMessage: any;
    scenarioId: number;
    currentSensitivity: any;
    currentScenario: any;
}

export function useGlobalWebSocketHandler({
    lastJsonMessage,
    scenarioId,
    currentSensitivity,
    currentScenario,
}: UseGlobalWebSocketHandlerParams): void {
    const queryClient = useQueryClient();

    const patchTree = useCallback(
        (updater: (prev: EditorState) => EditorState) =>
            patchTreeCache(queryClient, scenarioId, updater),
        [queryClient, scenarioId],
    );

    useEffect(() => {
        if (!lastJsonMessage) return;
        const msg = lastJsonMessage as any;

        // ── scenario_computed — invalidate section caches ──
        if (msg.type === 'scenario_computed' && !currentSensitivity) {
            queryClient.invalidateQueries({
                queryKey: [SECTION_DATA_KEY, scenarioId],
            });
        }

        // ── sensitivity_computed — invalidate section caches ──
        if (msg.type === 'sensitivity_computed' && currentSensitivity && currentSensitivity.id == msg.sensitivity_id) {
            queryClient.invalidateQueries({
                queryKey: [SECTION_DATA_KEY, scenarioId],
            });
        }

        // ── scenario_currently_computing — clear input + output errors ──
        // Input errors are also cleared here so that a successful recompute
        // (which only emits `scenario_computed`, never `scenario_input_errors`
        // when the error list is empty) doesn't leave stale per-field warning
        // icons in the tree. New errors, if any, are re-applied by the
        // `scenario_input_errors` handler below.
        if (msg.type === 'scenario_currently_computing') {
            patchTree((prev) => ({
                ...prev,
                dashboardComputed: false,
                Input: {
                    ...prev.Input,
                    tree: { ...prev.Input.tree, items: clearTreeErrors(prev.Input.tree.items) },
                },
                Output: {
                    ...prev.Output,
                    tree: { ...prev.Output.tree, items: clearTreeErrors(prev.Output.tree.items) },
                },
            }));
        }

        // ── sensitivity_currently_computing — clear input + output errors ──
        if (msg.type === 'sensitivity_currently_computing' && currentSensitivity) {
            patchTree((prev) => ({
                ...prev,
                Input: {
                    ...prev.Input,
                    tree: { ...prev.Input.tree, items: clearTreeErrors(prev.Input.tree.items) },
                },
                Output: {
                    ...prev.Output,
                    tree: { ...prev.Output.tree, items: clearTreeErrors(prev.Output.tree.items) },
                },
            }));
        }

        // ── scenario_input_errors — apply field-level errors to Input tree ──
        if (msg.type === 'scenario_input_errors') {
            patchTree((prev) => {
                const newInputItems = clearTreeErrors(prev.Input.tree.items);
                for (const err of msg.errors) {
                    const item = newInputItems[err.model_data_id.toString()];
                    if (item && item.fields?.[err.field_name]) {
                        newInputItems[err.model_data_id.toString()] = {
                            ...item,
                            compute_error: err.error,
                            fields: {
                                ...item.fields,
                                [err.field_name]: { ...item.fields[err.field_name], compute_error: err.error },
                            },
                        };
                    }
                }
                return {
                    ...prev,
                    Input: {
                        ...prev.Input,
                        tree: { ...prev.Input.tree, items: newInputItems },
                    },
                };
            });
        }

        // ── sensitivity_input_errors ──
        if (msg.type === 'sensitivity_input_errors' && currentSensitivity && currentSensitivity.id == msg.sensitivity_id) {
            patchTree((prev) => {
                const newInputItems = clearTreeErrors(prev.Input.tree.items);
                for (const err of msg.errors) {
                    const item = newInputItems[err.model_data_id.toString()];
                    if (item && item.fields?.[err.field_name]) {
                        newInputItems[err.model_data_id.toString()] = {
                            ...item,
                            compute_error: err.error,
                            fields: {
                                ...item.fields,
                                [err.field_name]: { ...item.fields[err.field_name], compute_error: err.error },
                            },
                        };
                    }
                }
                return {
                    ...prev,
                    Input: {
                        ...prev.Input,
                        tree: { ...prev.Input.tree, items: newInputItems },
                    },
                };
            });
        }

        // ── summary_computed — update tree-cache dashboardComputed flag (FME).
        // currentScenario.dashboard_computed (read by Dashboard.tsx) is refreshed
        // by the summary_computed handler in ModelContext via refreshModel().
        if (msg.type === 'summary_computed' && !msg.sensitivity_id) {
            patchTree((prev) => ({ ...prev, dashboardComputed: true }));
        }
    }, [lastJsonMessage, scenarioId, currentSensitivity, currentScenario, queryClient, patchTree]);
}
